import { useMemo, useSyncExternalStore } from 'react';
import type { StoreType } from '../../../redux/store';
import {
  isStoppedError,
  MasterPartError,
  MasterPartsStop,
  uploadMasterParts,
} from '../../smartLinks/catalog/song/masterUploadParts';
import {
  footageQuotaFacts,
  footageUploadError,
  type FootageQuotaFacts,
  type FootageUploadError,
} from '../doc/musicVideoErrors';
import { musicVideoEditorApi } from '../musicVideoEditorApi';
import { footageContentType, footageFileRefusal } from './footageFiles';

/**
 * The footage uploads of the editor (design c2, spec §4.3). They live outside React, like the
 * "Add yours" uploads (`addedUploadStore.ts`): a switch of the tool, a closed panel or a leave of
 * the editor keeps each upload going, and the Upload tab shows the progress, Stop and the failures
 * again when it opens. Only Stop ends an upload. While an upload runs, a reload or a closed tab
 * asks first (`beforeunload`): the browser would end the upload.
 *
 * Each file goes from the browser straight to S3 in presigned parts (`uploadMasterParts`), then
 * `.../complete` checks it with ffprobe and makes the clip, which shows in Yours when it is ready.
 */
export interface FootageUploadItem {
  id: string;
  accountId: number;
  catalogItemUuid: string;
  name: string;
  sizeBytes: number;
  sentBytes: number;
  phase: 'uploading' | 'checking' | 'failed';
  error: FootageUploadError | null;
  /** The storage numbers of a `quota` refusal, when the server gave them. */
  quota: FootageQuotaFacts | null;
}

let uploads: readonly FootageUploadItem[] = [];
const stops = new Map<string, MasterPartsStop>();
const listeners = new Set<() => void>();
let nextUploadId = 0;

function isRunning(item: FootageUploadItem): boolean {
  return item.phase !== 'failed';
}

function warnBeforeUnload(event: BeforeUnloadEvent): void {
  event.preventDefault();
  // An old browser shows the prompt only with a value.
  // eslint-disable-next-line no-param-reassign
  event.returnValue = '';
}

let isUnloadGuarded = false;

/** A reload or a closed tab asks first while an upload runs. */
function syncUnloadGuard(): void {
  const shouldGuard = uploads.some(isRunning);
  if (shouldGuard === isUnloadGuarded || typeof window === 'undefined') {
    return;
  }
  isUnloadGuarded = shouldGuard;
  if (shouldGuard) {
    window.addEventListener('beforeunload', warnBeforeUnload);
  } else {
    window.removeEventListener('beforeunload', warnBeforeUnload);
  }
}

function setUploads(next: readonly FootageUploadItem[]): void {
  uploads = next;
  syncUnloadGuard();
  listeners.forEach((listener) => listener());
}

function patch(id: string, change: Partial<FootageUploadItem>): void {
  if (uploads.some((item) => item.id === id)) {
    setUploads(uploads.map((item) => (item.id === id ? { ...item, ...change } : item)));
  }
}

/** Forgets an upload (a done one, a stopped one, or a failure the user closed). */
export function dismissFootageUpload(id: string): void {
  if (uploads.some((item) => item.id === id)) {
    setUploads(uploads.filter((item) => item.id !== id));
  }
}

/** Stops a running upload: the part on the way, then the upload on the server. The row goes. */
export function stopFootageUpload(id: string): void {
  stops.get(id)?.stop();
}

/** True while an upload of the song (or of any song) works. */
export function hasRunningFootageUploads(catalogItemUuid?: string): boolean {
  return uploads.some(
    (item) => isRunning(item) && (catalogItemUuid === undefined || item.catalogItemUuid === catalogItemUuid)
  );
}

/** Every upload; a test resets the store with `resetFootageUploadsForTests`. */
export function getFootageUploads(): readonly FootageUploadItem[] {
  return uploads;
}

export function resetFootageUploadsForTests(): void {
  stops.clear();
  setUploads([]);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The uploads of one song, in the order they started. */
export function useFootageUploads(catalogItemUuid: string): FootageUploadItem[] {
  const all = useSyncExternalStore(subscribe, getFootageUploads, getFootageUploads);
  return useMemo(() => all.filter((item) => item.catalogItemUuid === catalogItemUuid), [all, catalogItemUuid]);
}

export interface FootageUploadDeps {
  dispatch: StoreType['dispatch'];
}

interface UploadOneInput {
  accountId: number;
  catalogItemUuid: string;
  file: File;
}

async function uploadOne({ dispatch }: FootageUploadDeps, { accountId, catalogItemUuid, file }: UploadOneInput) {
  nextUploadId += 1;
  const id = `upload-${nextUploadId}`;
  const refusal = footageFileRefusal(file);
  setUploads([
    ...uploads,
    {
      id,
      accountId,
      catalogItemUuid,
      name: file.name,
      sizeBytes: file.size,
      sentBytes: 0,
      phase: refusal === null ? 'uploading' : 'failed',
      error: refusal,
      quota: null,
    },
  ]);
  const contentType = footageContentType(file);
  if (refusal !== null || contentType === null) {
    return;
  }
  const stop = new MasterPartsStop();
  stops.set(id, stop);
  let started: { uploadId: string; key: string } | null = null;
  try {
    const upload = await dispatch(
      musicVideoEditorApi.endpoints.startFootageUpload.initiate({
        accountId,
        fileName: file.name,
        contentType,
        sizeBytes: file.size,
        catalogItemUuid,
      })
    ).unwrap();
    started = { uploadId: upload.uploadId, key: upload.key };
    const parts = await uploadMasterParts({
      file,
      parts: upload.parts,
      partSizeBytes: upload.partSizeBytes,
      onProgress: (sentBytes) => patch(id, { sentBytes }),
      stop,
    });
    // Nothing stops the complete call: the server makes the clip.
    stops.delete(id);
    patch(id, { phase: 'checking', sentBytes: file.size });
    await dispatch(
      musicVideoEditorApi.endpoints.completeFootageUpload.initiate({
        accountId,
        uploadId: upload.uploadId,
        key: upload.key,
        parts,
      })
    ).unwrap();
    dismissFootageUpload(id);
  } catch (error: unknown) {
    if (started !== null) {
      // The parts on S3 go: a failed or stopped upload leaves nothing behind.
      dispatch(musicVideoEditorApi.endpoints.abortFootageUpload.initiate({ accountId, ...started }))
        .unwrap()
        .catch(() => undefined);
    }
    if (isStoppedError(error) || stop.stopped) {
      dismissFootageUpload(id);
      return;
    }
    console.error('Music video editor: the footage upload failed', error);
    patch(id, {
      phase: 'failed',
      error: error instanceof MasterPartError ? 'network' : footageUploadError(error),
      quota: footageQuotaFacts(error),
    });
  } finally {
    stops.delete(id);
  }
}

/** Starts one upload for each file. Each runs by itself; a refused file shows its failure at once. */
export function startFootageUploads(
  deps: FootageUploadDeps,
  input: { accountId: number; catalogItemUuid: string; files: FileList | readonly File[] }
): void {
  Array.from(input.files).forEach((file) => {
    uploadOne(deps, { accountId: input.accountId, catalogItemUuid: input.catalogItemUuid, file }).catch(console.error);
  });
}
